import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { useFluidPaymentsStore } from '@/features/cashier/fluid/fluidPaymentsStore';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';

import { selectBtag, selectLocale } from '@/store/selectors/commonSelectors';
import { selectUiIsSingleGame } from '@/store/ui/selectors';
import { selectIsFtdExitIntentShown } from '@/store/modal/selectors';
import { setShowBalanceUpdate } from '@/store/modal/actions';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { getLimits } from '@/store/actions/limitsActions';
import { triggerNetreferFTD } from '@/shared/netrefer';
import { FluidCommandEvents, FluidInfoEvents } from '@/features/cashier/fluid/constants';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import { logException } from '@/utils/logger';
import { selectFluidConfig } from '@/store/selectors/combined.selectors';
import { selectFluidUserData, selectUserId } from '@/store/selectors/userSelectors';
import { selectLowestRemainingDepositLimit } from '@/store/selectors/limitsSelectors';
import { sendEventToAppWrapper } from '@/utils/app-wrapper-utils';
import { useLocation } from 'react-router-dom';
import { kycGetDocuments } from '@/store/actions/kycActions';
import { shouldDisplayFTDExitIntent } from '@/utils/logic.utils';
import {
  selectUserBalance,
  selectUserDepositCount,
  selectUserWithdrawableBalance,
} from '@/store/wallet/selectors';
import { getUserWallet, incrementUserDepositCount } from '@/store/wallet/actions';
import useVerificationMethod from '@/hooks/useVerificationMethod';
import { getUserTags } from '@/store/actions/user';
import {
  selectBonusesTurnedOverPercentage,
  selectFluidCashierBonuses,
  selectHasWelcomeBonuses,
} from '@/store/bonuses/selectors';
import { getBonuses } from '@/store/bonuses/actions';
import { envIsMexico } from '@/constants/constants';
import { selectIsUserTimeoutBlocked } from '@/store/selectors/authSelectors';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';

interface FluidDepositSuccessDetails {
  amount: number;
}

interface FluidTransactionSubmitted {
  amount: number;
  bonus: string | undefined;
  method: string;
  message: string;
  transaction: string;
}

type FluidEventDetails<T = {}> = {
  message: string;
} & T;

interface FluidEvent {
  detail: FluidEventDetails;
}

const FluidPaymentsWrapper = (): JSX.Element | null => {
  const dispatch = useDispatch();
  const { openModal } = useModal();
  // data points needed for Fluid widget initialization
  const { show, closeFluid, activeBonuses, setBonuses, transactionType } = useFluidPaymentsStore();
  const userData = useSelector(selectFluidUserData);
  const [isFluidInit, setIsFluidInit] = useState(false);
  const [showFluid, setShowFluid] = useState(false);
  const fluidConfig = useSelector(selectFluidConfig);
  const scriptLoaded = useRef(false);
  const ref = useRef<any>(null);
  const isInitialMount = useRef(true);
  // data points needed for hooks, or Fluid reactive props
  const bonuses = useSelector(selectFluidCashierBonuses);
  const locale = useSelector(selectLocale);
  const userId = useSelector(selectUserId);
  const isSingleGame = useSelector(selectUiIsSingleGame);
  const hasWelcomeBonuses = useSelector(selectHasWelcomeBonuses);
  const depositLimit = useSelector(selectLowestRemainingDepositLimit);
  const turnedOverPercentage = useSelector(selectBonusesTurnedOverPercentage);
  const depositCount = useSelector(selectUserDepositCount);
  const btag = useSelector(selectBtag);
  const isFtdExitIntentShown = useSelector(selectIsFtdExitIntentShown);
  const isUserTimeoutBlocked = useSelector(selectIsUserTimeoutBlocked);
  const balance = useSelector(selectUserBalance);
  const withdrawableBalance = useSelector(selectUserWithdrawableBalance);
  const { pathname } = useLocation();
  const { openVerification } = useVerificationMethod();
  const isRestricted = useRestrictedMode();
  // action helpers
  const navigate = useAbsoluteNavigate();

  const disableDeposit = envIsMexico() || isUserTimeoutBlocked || isRestricted;

  const updateUser = () => {
    dispatch(getUserWallet());
    dispatch(getBonuses());
    dispatch(getLimits());
    dispatch(kycGetDocuments());
    dispatch(getUserTags());
  };

  // this is needed because Fluid doesn't work well if bonuses are updated before Fluid is initialized
  useEffect(() => {
    if (isFluidInit && bonuses) {
      setBonuses(bonuses);
    }
  }, [isFluidInit, bonuses]);

  useEffect(() => {
    let script: HTMLScriptElement | null = null;
    if (window && document && !scriptLoaded.current) {
      triggerGTag(GTagEvents.cashier_init_start);
      script = document.createElement('script');
      script.src = 'https://get.fluidpayments.io/index.js';
      script.type = 'module';
      script.async = true;
      script.onload = () => {
        // Fluid script loaded
      };
      document.head.appendChild(script);

      scriptLoaded.current = true;
    }

    return () => {
      if (script) {
        script.remove();
      }
    };
  }, []);

  useLayoutEffect(() => {
    const fluidWidget = ref.current;

    if (fluidWidget) {
      const handleInfoEvent = (event: FluidEvent) => {
        if (!event.detail || !event.detail.message) return;
        switch (event.detail.message) {
          case FluidInfoEvents.deposit_success:
            const { amount } = event.detail as FluidEventDetails<FluidDepositSuccessDetails>;
            if (depositCount === 0) {
              triggerGTag(GTagEvents.first_deposit, {
                amount,
              });
              triggerNetreferFTD(btag, userId!, locale);
              sendEventToAppWrapper(GTagEvents.first_deposit, {
                userId,
                btag,
                amount,
                currencyCode: fluidConfig?.currencyCode,
              });
            } else {
              triggerGTag(GTagEvents.deposit_success, {
                amount,
              });
              sendEventToAppWrapper(GTagEvents.deposit_success, {
                userId,
                btag,
                amount,
                currencyCode: fluidConfig?.currencyCode,
              });
            }
            dispatch(incrementUserDepositCount());
            updateUser();
            if (isSingleGame) {
              dispatch(setShowBalanceUpdate(true));
            }
            break;
          case FluidInfoEvents.success_cta_click:
            closeFluid();
            // if the user is on the rewards-reel page we don't want to lead him to games
            // in all other cases we want to point him to games
            if (!pathname.includes('/rewards-wheel')) {
              navigate('/games');
            }
            break;
          case FluidInfoEvents.withdrawal_success:
            updateUser();
            triggerGTag(GTagEvents.cashier_withdrawal_success);
            break;
          case FluidInfoEvents.initialised:
            setIsFluidInit(true);
            triggerGTag(GTagEvents.cashier_ready);
            break;
        }
      };

      const handleCommandEvent = (event: FluidEvent) => {
        if (!event.detail || !event.detail.message) return;

        switch (event.detail.message) {
          case FluidCommandEvents.handle_transaction:
            const {
              amount: transactionAmount,
              bonus,
              message,
              transaction,
              method,
            } = event.detail as FluidEventDetails<FluidTransactionSubmitted>;
            if (
              message === 'handle-transaction' &&
              transaction === 'deposit' &&
              method === 'Trustly'
            ) {
              openModal(MODALS.PAYNPLAY_IFRAME, {
                amount: Number(transactionAmount),
                welcomeBonus: !!bonus,
                isDeposit: true,
              });
              closeFluid();
            } else {
              triggerGTag(GTagEvents.fluid_unhandled_case, {
                slug: `${message}-${transaction}-${method}`,
              });
            }
            break;
          case FluidCommandEvents.start_kyc_verification:
            closeFluid();
            openVerification();
            break;
          case FluidCommandEvents.close:
            if (
              !isRestricted &&
              shouldDisplayFTDExitIntent(
                depositCount,
                hasWelcomeBonuses,
                isFtdExitIntentShown,
                locale,
                userData.kycStatus,
              )
            ) {
              triggerGTag(GTagEvents.ftd_exit_intent_appeared);
              openModal(MODALS.EXIT_INTENT);
            } else {
              closeFluid();
            }
            break;
        }
      };

      const handleErrorEvent = (event: FluidEvent) => {
        // In the initial version of Fluid we will log every error to Sentry
        logException(new Error('Fluid error'), { error: event.detail });
        triggerGTag(GTagEvents.fluid_cashier_error, { error: event.detail.message });
      };

      fluidWidget.addEventListener('fluid-info', handleInfoEvent);
      fluidWidget.addEventListener('fluid-command', handleCommandEvent);
      fluidWidget.addEventListener('fluid-error', handleErrorEvent);

      return () => {
        fluidWidget.removeEventListener('fluid-info', handleInfoEvent);
        fluidWidget.removeEventListener('fluid-command', handleCommandEvent);
        fluidWidget.removeEventListener('fluid-error', handleErrorEvent);
      };
    }
  }, [
    depositCount,
    locale,
    btag,
    userId,
    isSingleGame,
    isFtdExitIntentShown,
    hasWelcomeBonuses,
    fluidConfig,
    pathname,
    userData.kycStatus,
    isRestricted,
  ]);

  // Handle triggering of GTM events on opening/closing of the wallet
  useEffect(() => {
    if (isInitialMount.current) {
      // Without this check, the cashier_drawer_close event
      // would be triggered when app/Fluid is loaded (on initial mount)
      isInitialMount.current = false;
    } else {
      if (show) {
        triggerGTag(GTagEvents.cashier_drawer_open);
      } else {
        triggerGTag(GTagEvents.cashier_drawer_close);
      }
    }
  }, [show]);

  // This useEffect's roles are:
  // 1. Re-fetch /bonuses for turned-over percentage
  // 2. Re-fetch the wallet to keep it updated with the money user spent in a game
  // 3. We re-fetch on isSingleGame only because it can happen that player had game rounds, and we want to present fresh data
  // 3.1. And we don't need to refresh data on other pages as we know nothing has changed
  useEffect(() => {
    if (show && isSingleGame) {
      dispatch(getUserWallet());
      dispatch(getBonuses());
    }
  }, [show, isSingleGame]);

  useEffect(() => {
    if (isFluidInit && show) {
      triggerGTag(GTagEvents.fluid_show_init);
    }
    if (!isFluidInit && show) {
      triggerGTag(GTagEvents.fluid_show_not_init);
    }
    setShowFluid(isFluidInit && show);
  }, [isFluidInit, show]);

  if (!fluidConfig) {
    return null;
  }

  return (
    <fluid-widget
      ref={ref}
      id="fluid-widget"
      operator-id={fluidConfig.operatorId}
      user-id={fluidConfig.userId}
      session-id={fluidConfig.sessionId}
      locale={fluidConfig.locale}
      country={fluidConfig.countryCode}
      currency={fluidConfig.currencyCode}
      transaction={disableDeposit ? 'withdrawal' : transactionType}
      open={showFluid}
      balance={balance !== null ? balance : ''}
      withdrawable-balance={withdrawableBalance}
      lock-transaction-type={disableDeposit}
      mode="wave"
      bonuses={JSON.stringify(activeBonuses)}
      turned-over-percentage={turnedOverPercentage !== null ? turnedOverPercentage : ''}
      deposit-limit={depositLimit !== null ? depositLimit : ''}
      user-data={JSON.stringify(userData)}
      z-index="100"
    ></fluid-widget>
  );
};

export default FluidPaymentsWrapper;
